// frontend/src/components/reporting/activity/ConversationDebriefPage.js — RPT-003
// Conversation Debrief View. Read-only. Branches on methodology, rendering the
// shared debrief bodies from ./DebriefContent (which wrap the existing
// print-document components, already fully read-only).
// Two entry paths render the same component (backend re-verifies scope
// regardless of which userId is in the URL):
//   - drill-down: /app/reports/activity/users/:userId/conversations/:conversationId
//   - self entry: /app/my-activity/conversations/:conversationId

import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useLocation, Link } from 'react-router-dom';
import { CaretRight } from '@phosphor-icons/react';
import { useAuth } from '../../../contexts/AuthContext';
import { useRoles } from '../../../hooks/useRoles';
import { useApiCall } from '../../../hooks/useApiCall';
import apiService from '../../../services/apiService';
import { BasicDebriefContent, RealDebriefContent } from './DebriefContent';
import '../../../styles/roleplay.css';
import '../../../styles/assessment.css';
import '../../../styles/activityReporting.css';

const ConversationDebriefPage = () => {
  const { user } = useAuth();
  const { userProfile } = useRoles();
  const { userId: routeUserId, conversationId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const isSelfEntry = !routeUserId;
  const effectiveUserId = routeUserId || userProfile?.id;
  const targetTenantId = searchParams.get('targetTenantId') || userProfile?.tenant?.id || null;
  const conversationTitle = location.state?.conversationTitle;

  const [data, setData] = useState(null);
  const { loading, error, makeRequest } = useApiCall();

  const loadDebrief = useCallback(async () => {
    if (!effectiveUserId || !conversationId || !targetTenantId) return;
    try {
      const result = await makeRequest(
        () => apiService.get(
          `/api/v1/reporting/user-activity/${effectiveUserId}/conversations/${conversationId}?targetTenantId=${targetTenantId}`,
          user
        ),
        'Loading debrief…'
      );
      if (result.success) setData(result.data);
    } catch (err) {
      console.error('Failed to load conversation debrief:', err);
    }
  }, [effectiveUserId, conversationId, targetTenantId, user, makeRequest]);

  useEffect(() => { loadDebrief(); }, [loadDebrief]);

  const handlePrint = () => window.print();

  const backLink = location.state?.backTo
    ?? (isSelfEntry
      ? '/app/my-activity'
      : `/app/reports/activity/users/${routeUserId}?targetTenantId=${targetTenantId}`);
  const backLabel = location.state?.backLabel
    ?? (isSelfEntry ? 'My Activity' : 'Back to conversations');

  return (
    <div className="activity-report-page">
      <div className="arp-breadcrumb">
        <Link to={backLink} className="arp-breadcrumb-link">
          {backLabel}
        </Link>
        <CaretRight size={11} weight="bold" />
        <span>{conversationTitle || 'Conversation Debrief'}</span>
      </div>

      <div className="arp-header">
        <div>
          <h1 className="arp-title">{conversationTitle || 'Conversation Debrief'}</h1>
          <p className="arp-subtitle">Read-only — no edits are made from this view.</p>
        </div>
        <div className="arp-actions">
          <button className="arp-btn arp-btn-secondary" onClick={handlePrint} disabled={!data}>
            Print / Save as PDF
          </button>
        </div>
      </div>

      {error && <div className="arp-error-state">{error}</div>}

      {loading ? (
        <div className="arp-state">Loading…</div>
      ) : !data ? (
        <div className="arp-state">Conversation not found.</div>
      ) : data.methodology === 'basic' ? (
        <BasicDebriefContent data={data} conversationTitle={conversationTitle} />
      ) : (
        <RealDebriefContent data={data} conversationTitle={conversationTitle} />
      )}
    </div>
  );
};

export default ConversationDebriefPage;
